import { useState } from "react"
import { Box, Card, Checkbox, Collapse, FormControlLabel, Stack, Typography } from "@mui/material"
import { ChevronDown, ChevronRight } from "lucide-react"
import { VERTICAL_LABELS } from "@xop/dashboard-shared"

import { RELATIONSHIP_FACETS, RELATIONSHIP_LABELS, type RelationshipFacet } from "./facets"
import type { ContactGroupOption } from "./audience-api"

/** One row of a facet checklist: label + a per-value population count. */
interface FacetRowProps {
  label: string
  checked: boolean
  count: number
  disabled?: boolean
  onToggle: () => void
}

function FacetRow({ label, checked, count, disabled = false, onToggle }: FacetRowProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <FormControlLabel
        control={<Checkbox size="small" checked={checked} disabled={disabled} onChange={onToggle} />}
        label={label}
        sx={{ flex: 1, mr: 0 }}
      />
      <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 32, textAlign: "right" }}>
        {Math.round(count).toLocaleString()}
      </Typography>
    </Box>
  )
}

export interface IndustryOption {
  industry: string
  count: number
}

export interface FacetRailProps {
  relationships: RelationshipFacet[]
  relationshipCounts: Partial<Record<RelationshipFacet, number>>
  groups: number[]
  groupOptions: ContactGroupOption[]
  industries: string[]
  industryOptions: IndustryOption[]
  disabled?: boolean
  onRelationshipToggle: (facet: RelationshipFacet) => void
  onSelectAllRelationships: (selectAll: boolean) => void
  onGroupToggle: (groupId: number) => void
  onIndustryToggle: (industry: string) => void
}

/** Joins the canonical vertical labels with counts from `fetchIndustries`,
 * showing 0 when a canonical label has no matching contacts. */
function canonicalIndustryOptions(industryOptions: IndustryOption[]): { label: string; count: number }[] {
  const countByLabel = new Map(industryOptions.map((option) => [option.industry, option.count]))
  return VERTICAL_LABELS.map((label) => ({ label, count: countByLabel.get(label) ?? 0 }))
}

/**
 * Left rail of the Audience Builder. The Relationship section is a 3-layer tree:
 *   Relationship
 *     ├─ the relationship facets (Customers / Prospects / … / Standalone)
 *     └─ Contact groups (collapsible)
 *          └─ each saved group
 * Then the Industry facet — rendered from the CANONICAL vertical list
 * (`@xop/dashboard-shared`), not whatever the backend returns, so the operator
 * always sees one consistent vocabulary.
 */
export function FacetRail({
  relationships,
  relationshipCounts,
  groups,
  groupOptions,
  industries,
  industryOptions,
  disabled = false,
  onRelationshipToggle,
  onSelectAllRelationships,
  onGroupToggle,
  onIndustryToggle,
}: FacetRailProps) {
  const [groupsExpanded, setGroupsExpanded] = useState(() => groups.length > 0)

  const allSelected = relationships.length === RELATIONSHIP_FACETS.length
  const someSelected = relationships.length > 0 && !allSelected
  const canonicalIndustries = canonicalIndustryOptions(industryOptions)

  return (
    <Card variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Relationship
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              disabled={disabled}
              onChange={(e) => onSelectAllRelationships(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              All contacts
            </Typography>
          }
          sx={{ mb: 0.5 }}
        />
        <Stack spacing={0.25} sx={{ pl: 2 }}>
          {RELATIONSHIP_FACETS.map((facet) => (
            <FacetRow
              key={facet}
              label={RELATIONSHIP_LABELS[facet]}
              checked={relationships.includes(facet)}
              count={relationshipCounts[facet] ?? 0}
              disabled={disabled}
              onToggle={() => onRelationshipToggle(facet)}
            />
          ))}

          {/* Contact groups: layer-2 tree node (sibling of the relationship
              facets) whose children are the individual saved groups. */}
          <Box>
            <Box
              onClick={() => !disabled && setGroupsExpanded((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                py: 0.5,
                cursor: disabled ? "default" : "pointer",
                userSelect: "none",
              }}
            >
              {groupsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Typography variant="body2" sx={{ flex: 1 }}>
                Contact groups
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {groups.length > 0 ? `${groups.length} selected` : Math.round(groupOptions.length).toLocaleString()}
              </Typography>
            </Box>
            <Collapse in={groupsExpanded} unmountOnExit>
              <Stack spacing={0.25} sx={{ pl: 3, maxHeight: 200, overflowY: "auto" }}>
                {groupOptions.length === 0 ? (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    No saved contact groups yet.
                  </Typography>
                ) : (
                  groupOptions.map((group) => (
                    <FacetRow
                      key={group.id}
                      label={group.name}
                      checked={groups.includes(group.id)}
                      count={group.count}
                      disabled={disabled}
                      onToggle={() => onGroupToggle(group.id)}
                    />
                  ))
                )}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Industry
        </Typography>
        <Stack spacing={0.25} sx={{ maxHeight: 260, overflowY: "auto" }}>
          {canonicalIndustries.map(({ label, count }) => (
            <FacetRow
              key={label}
              label={label}
              checked={industries.includes(label)}
              count={count}
              disabled={disabled}
              onToggle={() => onIndustryToggle(label)}
            />
          ))}
        </Stack>
      </Box>
    </Card>
  )
}

export default FacetRail
